<template>
  <span class="odometer tnum" :aria-label="text" role="img">
    <span
      v-for="c in chars"
      :key="c.key"
      :class="['odo-cell', { 'is-fraction': c.fraction, 'is-sep': c.digit === null }]"
      aria-hidden="true"
    >
      <span
        v-if="c.digit !== null"
        class="odo-strip"
        :style="{ transform: `translateY(${-(mounted ? c.digit : 0) * 10}%)`, transitionDelay: `${(c.key % 6) * 40}ms` }"
      >
        <span v-for="n in 10" :key="n">{{ n - 1 }}</span>
      </span>
      <template v-else>{{ c.ch }}</template>
    </span>
  </span>
</template>

<script lang="ts">
import { defineComponent, computed, onMounted, ref } from 'vue'

export default defineComponent({
  name: 'Odometer',
  props: {
    value: {
      type: [Number, String],
      default: 0
    },
    decimals: {
      type: Number,
      default: 0
    },
    group: {
      type: Boolean,
      default: true
    }
  },
  setup(props) {
    const mounted = ref(false)

    onMounted(() => {
      const settle = () => { mounted.value = true }
      if (typeof document !== 'undefined' && document.hidden) return settle()
      requestAnimationFrame(settle)
      setTimeout(settle, 120)
    })

    const text = computed(() => {
      if (props.value === null || props.value === undefined || Number.isNaN(Number(props.value))) return '—'
      return Number(props.value).toLocaleString('en-US', {
        minimumFractionDigits: props.decimals,
        maximumFractionDigits: props.decimals,
        useGrouping: props.group,
      })
    })

    const chars = computed(() => {
      const list = [...text.value]
      const dot = list.indexOf('.')
      return list.map((ch, i) => ({
        ch,
        key: list.length - i,
        digit: /\d/.test(ch) ? Number(ch) : null,
        fraction: dot !== -1 && i > dot,
      }))
    })

    return {
      mounted,
      text,
      chars
    }
  }
})
</script>

<style scoped lang="scss">
.odometer {
  display: inline-flex;
  align-items: baseline;
  line-height: 1;
  font-family: var(--font-mono);
}
.odo-cell {
  position: relative;
  display: inline-block;
  height: 1em;
  overflow: hidden;
}
.odo-cell.is-sep {
  overflow: visible;
}
.odo-cell.is-fraction {
  color: var(--ink-faint);
}
.odo-strip {
  display: flex;
  flex-direction: column;
  transition: transform 0.9s cubic-bezier(0.16, 1, 0.3, 1);
}
.odo-strip > span {
  display: block;
  height: 1em;
  line-height: 1;
  text-align: center;
}
</style>
